/**
 * /new — AI onboarding v2: one fixed shell, no page scroll.
 *
 * Phases: compose (chat box) → generating (theater) → result. The result
 * phase is a horizontal step slider — Look → Name & link → Strip → Colour →
 * AI moment — inside a fixed h-screen shell; only step contents scroll.
 * The header rail navigates back to any visited step (and to the prompt).
 *
 * THE URL IS THE POSITION. `?step=prompt|look|name|strip|colour|effect` drives
 * the phase and the slider, and every move pushes a history entry, so the
 * browser back button walks back through the wizard instead of leaving the
 * site (`/` → `/new` is a `replace`, so back used to be a trapdoor out of the
 * product). It also makes a restored draft resume on the step it was left on,
 * and makes a step linkable.
 *
 * The booth preview is owned HERE, not by the steps: a single <PreviewPane>
 * sits beside the slider (a side rail on desktop, a band above it on narrow
 * screens) so every step edits something the creator can see. Steps render
 * controls only — none of them mount their own preview.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FaArrowLeft, FaArrowRight, FaSpinner, FaGoogle, FaUndo, FaTimes } from 'react-icons/fa'
import { useOnboardingStore, RESULT_STEPS, isDrawnFrameId } from './onboardingStore'
import {
  fetchOnboardingCatalog,
  fetchStudioSession,
  fetchClarifyQuestions,
  generateBooth,
  regenerateAsset,
  createOnboardingProject,
  ApiError,
  TimeoutError,
  type OnboardingCatalog,
  type CatalogFrame,
  type RegenTarget,
  type StudioSessionUser,
  type Clarification,
  type ClarifyQuestion
} from './onboardingApi'
import { signInWithStudio } from './studioAuth'
import { clearStudioAuth } from './studioToken'
import { publicBoothHostLabel } from './publicOrigin'
import { generationLocale, useCopy, type Copy } from './regionCopy'
import NewNavbar from './components/NewNavbar'
import LangToggle from './components/LangToggle'
import MyBooths from './components/MyBooths'
import PreviewPane from './components/PreviewPane'
import GoLiveStrip, { type GoLiveSnapshot } from './components/GoLiveStrip'
import PromptComposer from './components/PromptComposer'
import ClarifyCard from './components/ClarifyCard'
import GenerationTheater from './components/GenerationTheater'
import StepBackgrounds from './steps/StepBackgrounds'
import StepBasics from './steps/StepBasics'
import StepFramesV2 from './steps/StepFramesV2'
import StepFiltersV2 from './steps/StepFiltersV2'
import StepEffects from './steps/StepEffects'

const EMPTY_CATALOG: OnboardingCatalog = {
  mode: null,
  frames: [],
  filters: { official: [], community: [] }
}

// Named for what the creator walks away with, not for the system behind it.
const stepLabels = (c: Copy): Record<(typeof RESULT_STEPS)[number], string> => ({
  backgrounds: c.stepLook,
  basics: c.stepName,
  frames: c.stepStrip,
  filters: c.stepColour,
  effects: c.stepEffect
})

/**
 * URL slugs for the steps, index-aligned with RESULT_STEPS. Short and
 * human — this ends up in the address bar of the first page a creator sees.
 */
const STEP_SLUGS = ['look', 'name', 'strip', 'colour', 'effect'] as const
const PROMPT_SLUG = 'prompt'

function stepIndexFromSlug(slug: string | null): number {
  const i = STEP_SLUGS.indexOf((slug ?? '') as (typeof STEP_SLUGS)[number])
  return i < 0 ? 0 : i
}

const Onboarding: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const urlStep = searchParams.get('step')
  const phase = useOnboardingStore((s) => s.phase)
  const stepIndex = useOnboardingStore((s) => s.stepIndex)
  const name = useOnboardingStore((s) => s.name)
  const slug = useOnboardingStore((s) => s.slug)
  const slugStatus = useOnboardingStore((s) => s.slugStatus)
  const submitting = useOnboardingStore((s) => s.submitting)
  const error = useOnboardingStore((s) => s.error)
  const restored = useOnboardingStore((s) => s.restored)
  const spec = useOnboardingStore((s) => s.spec)
  const c = useCopy()
  const STEP_LABELS = stepLabels(c)

  const [catalog, setCatalog] = useState<OnboardingCatalog>(EMPTY_CATALOG)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogLoaded, setCatalogLoaded] = useState(false)
  const [catalogAttempt, setCatalogAttempt] = useState(0)
  const [sessionUser, setSessionUser] = useState<StudioSessionUser | null>(null)
  const [clarify, setClarify] = useState<{
    draftId: string
    questions: ClarifyQuestion[]
  } | null>(null)
  const [clarifyLoading, setClarifyLoading] = useState(false)
  const [myFrames, setMyFrames] = useState<CatalogFrame[]>([])
  // Set once the booth exists — swaps the whole shell for the finish screen.
  const [live, setLive] = useState<GoLiveSnapshot | null>(null)
  const signedInEmail = sessionUser?.email ?? null

  /** Move the wizard by moving the URL — never by setting phase directly. */
  const goToStep = useCallback(
    (index: number, replace = false): void => {
      const safe = Math.max(0, Math.min(STEP_SLUGS.length - 1, index))
      navigate(`/new?step=${STEP_SLUGS[safe]}`, { replace })
    },
    [navigate]
  )
  const goToPrompt = useCallback(
    (replace = false): void => {
      navigate(`/new?step=${PROMPT_SLUG}`, { replace })
    },
    [navigate]
  )

  // URL → store. The single direction of truth for phase/step; every control
  // navigates and this brings the store along, so Back and Forward work for
  // free. `generating` is deliberately exempt — the theater owns the screen
  // until the request settles.
  useEffect(() => {
    const st = useOnboardingStore.getState()
    if (st.phase === 'generating') return

    if (!urlStep) {
      // Fresh load with a bare /new: pin the position so back/forward has
      // something to walk, resuming a restored draft on its own step.
      if (st.spec) goToStep(st.stepIndex, true)
      else goToPrompt(true)
      return
    }
    if (urlStep === PROMPT_SLUG) {
      if (st.phase !== 'compose') st.setPhase('compose')
      return
    }
    // A step URL without a booth behind it (stale link, cleared draft) can
    // only mean the prompt.
    if (!st.spec) {
      goToPrompt(true)
      return
    }
    if (st.phase !== 'result') st.setPhase('result')
    const next = stepIndexFromSlug(urlStep)
    if (next !== st.stepIndex) st.setStepIndex(next)
  }, [urlStep, goToStep, goToPrompt])

  useEffect(() => {
    let cancelled = false
    fetchStudioSession().then((user) => {
      if (!cancelled) setSessionUser(user)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Leave guard. Armed whenever there is a booth (or a written prompt) that
  // has not been turned into a project yet. The sign-in popup does NOT unload
  // the opener, so this never fires on the OAuth round-trip.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (live) return
      if (!useOnboardingStore.getState().hasUnsavedWork()) return
      e.preventDefault()
      // Legacy browsers require a return value to show the prompt; the text
      // itself is ignored by every modern browser.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [live])

  // /new might be the first Dreambooth page a creator ever sees — give it a
  // real title. Booth pages set their own on project load (ProjectRoute).
  useEffect(() => {
    const prev = document.title
    document.title = c.documentTitle
    return () => {
      document.title = prev
    }
  }, [c.documentTitle])

  useEffect(() => {
    if (phase !== 'result' || catalogLoaded) return
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    setCatalogLoading(true)
    fetchOnboardingCatalog(useOnboardingStore.getState().primaryMode())
      .then((data) => {
        if (cancelled) return
        setCatalog(data)
        setCatalogLoaded(true)
        const st = useOnboardingStore.getState()
        if (st.filterIds.length === 0) {
          const normal = data.filters.official.find((f) => f.name.trim().toLowerCase() === 'normal')
          if (normal) st.setFilterIds([normal._id])
        }
      })
      .catch((err) => {
        console.error('[onboarding] catalog load failed:', err)
        // One failure used to be permanent — the deps never changed again,
        // so the Colour step sat on an empty catalog forever. Retry a few
        // times; a bumped attempt count re-runs this effect.
        if (!cancelled && catalogAttempt < 4) {
          retryTimer = setTimeout(() => setCatalogAttempt((a) => a + 1), 5000)
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false)
      })
    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [phase, catalogLoaded, catalogAttempt])

  /** Shared sign-in gate (generate, frames modal, effects, create). */
  const ensureSignIn = async (): Promise<boolean> => {
    if (signedInEmail) return true
    const user = await signInWithStudio()
    if (user) {
      setSessionUser(user)
      return true
    }
    return false
  }

  /**
   * A 401 from the studio means the cached bearer token is dead. Clearing it
   * is what lets the NEXT ensureSignIn() actually open the popup —
   * fetchStudioSession() prefers the stored token, so a stale one made every
   * retry return the same expired identity without ever asking Google again.
   */
  const dropDeadSession = (): void => {
    clearStudioAuth()
    setSessionUser(null)
  }

  const doGenerate = async (draftId?: string, clarifications?: Clarification[]): Promise<void> => {
    const st = useOnboardingStore.getState()
    st.startGenerating()
    try {
      const res = await generateBooth({
        draftId: draftId ?? st.draftId ?? undefined,
        prompt: st.prompt.trim(),
        locale: generationLocale(),
        logoDataUrl: st.logoDataUrl ?? undefined,
        attachments: st.attachments.map((a) => ({
          dataUrl: a.dataUrl,
          name: a.name,
          role: a.role
        })),
        clarifications: clarifications?.length ? clarifications : undefined
      })
      const store = useOnboardingStore.getState()
      store.applyGeneration(res)
      store.setStepIndex(0)
      goToStep(0)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) dropDeadSession()
      const message =
        err instanceof TimeoutError
          ? c.generationTimedOut
          : err instanceof ApiError && err.status === 401
            ? c.signInToBuild
            : err instanceof Error
              ? err.message
              : c.generationFailed
      useOnboardingStore.getState().generationFailed(message)
      // generationFailed() drops back to compose when there is no earlier
      // design to return to; the URL has to follow or the shell and the
      // address bar disagree about where the creator is.
      if (!useOnboardingStore.getState().spec) goToPrompt()
      else goToStep(useOnboardingStore.getState().stepIndex)
    }
  }

  const handleGenerate = async (): Promise<void> => {
    const st = useOnboardingStore.getState()
    // Sign-in BEFORE any generation (2026-08-16 decision). Anonymous
    // generation burned tokens on visitors who never converted and could
    // never be contacted; the account comes first now, then the model runs.
    st.setError(null)
    const ok = await ensureSignIn()
    if (!ok) {
      st.setError(c.signInToBuild)
      return
    }
    // A repeat generation on an existing draft skips clarify — the user is
    // iterating, not starting fresh.
    if (st.draftId) {
      await doGenerate()
      return
    }
    setClarifyLoading(true)
    const res = await fetchClarifyQuestions({
      prompt: st.prompt.trim(),
      locale: generationLocale()
    })
    setClarifyLoading(false)
    if (res && res.questions.length > 0) {
      setClarify(res)
      return
    }
    // No questions (or clarify failed) → straight to generation. Fail-open.
    await doGenerate(res?.draftId)
  }

  const handleRegen = async (target: RegenTarget, hint?: string): Promise<void> => {
    const st = useOnboardingStore.getState()
    if (!st.draftId) return
    st.setError(null)
    st.setRegenInFlight(target)
    try {
      const res = await regenerateAsset({ draftId: st.draftId, regenTarget: target, hint })
      useOnboardingStore.getState().applyRegen(res)
    } catch (err) {
      const st2 = useOnboardingStore.getState()
      st2.setRegenInFlight(null)
      if (err instanceof ApiError && err.status === 401) {
        dropDeadSession()
        st2.setError(c.signInToBuild)
      } else if (err instanceof TimeoutError) {
        st2.setError(c.regenerationFailed)
      } else {
        st2.setError(err instanceof Error ? err.message : c.regenerationFailed)
      }
    }
  }

  /**
   * Footer Back/Continue. Clears any stale action error first — a regen
   * failure message used to stay pinned in the footer across every later
   * step because nothing ever cleared it.
   */
  const handleStepNav = (index: number): void => {
    useOnboardingStore.getState().setError(null)
    goToStep(index)
  }

  /** Open the prompt to build again. Reversible — see handleBackToBooth. */
  const handleRefine = (): void => goToPrompt()

  /** The way out of the prompt that used to not exist. */
  const handleBackToBooth = (): void => goToStep(useOnboardingStore.getState().stepIndex)

  const handleStartOver = (): void => {
    const st = useOnboardingStore.getState()
    const message = st.spec ? c.startOverConfirmBooth : c.startOverConfirmPrompt
    if (!window.confirm(message)) return
    st.startOver()
    setClarify(null)
    setMyFrames([])
    goToPrompt(true)
  }

  const handleCreate = async (): Promise<void> => {
    const st = useOnboardingStore.getState()
    if (!st.spec) return
    st.setSubmitting(true)
    st.setError(null)
    try {
      const ok = await ensureSignIn()
      if (!ok) {
        st.setSubmitting(false)
        st.setError(c.signInNotCompleted)
        return
      }
      // Snapshot before discardDraft() wipes the store — the finish screen
      // renders the booth's own welcome design on the strip.
      const snapshot = {
        title: st.name.trim(),
        components: st.welcomeDesign?.orientedComponents.portrait ?? [],
        palette: st.spec.palette
      }
      const createdSlug = await createOnboardingProject({
        mode: st.primaryMode(),
        title: st.name.trim(),
        slug: st.slug,
        draftId: st.draftId ?? undefined,
        theme: {
          primaryColor: st.spec.palette.primaryColor,
          secondaryColor: st.spec.palette.secondaryColor,
          backgroundColor: st.spec.palette.backgroundColor
        },
        captureMode: st.captureMode ?? undefined,
        aiEffectId: st.aiEffectId ?? undefined,
        // The booth's own frames are attached server-side from the draft —
        // their client-side ids are synthetic placeholders.
        frameIds: st.frameIds.filter((id) => !isDrawnFrameId(id)),
        filterIds: st.filterIds
      })
      useOnboardingStore.getState().discardDraft()
      setLive({ ...snapshot, slug: createdSlug })
    } catch (err) {
      const st2 = useOnboardingStore.getState()
      st2.setSubmitting(false)
      if (err instanceof ApiError && err.status === 409) {
        st2.setSlugStatus('taken')
        // Move by moving the URL (the file-wide invariant) — a raw
        // setStepIndex left the address bar on ?step=effect while the slider
        // showed Basics, and the next Back went somewhere surprising.
        goToStep(RESULT_STEPS.indexOf('basics'))
        st2.setError(c.slugJustTaken)
      } else if (err instanceof ApiError && err.status === 401) {
        // Clear the dead token too, or every retry short-circuits on the
        // cached identity and the Google popup never reopens.
        dropDeadSession()
        st2.setError(c.signInExpired)
      } else {
        st2.setError(err instanceof Error ? err.message : c.somethingWentWrong)
      }
    }
  }

  /**
   * The draft has always been restored from localStorage; nothing ever said
   * so, which made a resumed session indistinguishable from a lost one.
   */
  const restoreNotice = restored ? (
    <div className="flex items-center justify-center gap-3 border-b border-blueSparkle/20 bg-blueSparkle/10 px-6 py-2 text-[12px] text-whiteout/80">
      <span>
        {spec ? c.restoredBooth : c.restoredPrompt}
      </span>
      <button
        type="button"
        onClick={handleStartOver}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-0.5 text-[11px] font-medium text-whiteout/80 transition-colors hover:border-white/40 hover:text-whiteout"
      >
        <FaUndo className="text-[9px]" /> {c.startOver}
      </button>
      <button
        type="button"
        onClick={() => useOnboardingStore.getState().dismissRestored()}
        aria-label={c.dismiss}
        className="text-whiteout/40 transition-colors hover:text-whiteout"
      >
        <FaTimes className="text-[11px]" />
      </button>
    </div>
  ) : null

  const isLastStep = stepIndex === RESULT_STEPS.length - 1
  const basicsValid = name.trim().length >= 2 && slug.length >= 3 && slugStatus === 'available'
  const canContinue = RESULT_STEPS[stepIndex] === 'basics' ? basicsValid : true
  const canCreate = basicsValid && !submitting

  if (live) {
    return (
      <div className="flex h-screen w-full flex-col overflow-y-auto soft-scrollbar bg-black text-whiteout">
        <NewNavbar user={sessionUser} onSignIn={() => void ensureSignIn()} />
        <div className="flex flex-1 items-center justify-center">
          <GoLiveStrip {...live} onOpen={() => navigate(`/${live.slug}`)} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-black text-whiteout">
      {phase === 'compose' && (
        <div className="min-h-0 flex-1 overflow-y-auto soft-scrollbar">
          <NewNavbar user={sessionUser} onSignIn={() => void ensureSignIn()} />
          {restoreNotice}
          {clarify ? (
            <ClarifyCard
              questions={clarify.questions}
              busy={false}
              onSubmit={(clarifications) => {
                const draftId = clarify.draftId
                setClarify(null)
                void doGenerate(draftId, clarifications)
              }}
              onSkip={() => {
                const draftId = clarify.draftId
                setClarify(null)
                void doGenerate(draftId)
              }}
            />
          ) : (
            <>
              <PromptComposer
                onGenerate={handleGenerate}
                busy={clarifyLoading}
                signedOut={!signedInEmail}
                onBackToBooth={spec ? handleBackToBooth : undefined}
              />
              <MyBooths signedIn={Boolean(sessionUser)} />
            </>
          )}
        </div>
      )}

      {phase === 'generating' && (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <GenerationTheater />
        </div>
      )}

      {phase === 'result' && (
        <>
          {restoreNotice}
          {/* Header rail */}
          <header className="shrink-0 border-b border-white/10 px-6 py-3">
            <div className="mx-auto flex w-full max-w-5xl items-center gap-2">
              <img
                src="/assets/images/dreambooth-icon-white.png"
                alt="Dreambooth"
                className="mr-1 h-5 w-5 shrink-0 opacity-80"
                draggable={false}
              />
              {/* "Prompt" read as "show me my prompt" and was a one-way door.
                  It says what it does now, and compose has a way back. */}
              <button
                type="button"
                onClick={handleRefine}
                title={c.editPromptHint}
                className="mr-2 shrink-0 rounded-full border border-white/10 px-3 py-1 text-[11px] text-whiteout/50 hover:border-white/30 hover:text-whiteout"
              >
                {c.editPrompt}
              </button>
              {RESULT_STEPS.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => goToStep(i)}
                  className="flex flex-1 flex-col gap-1"
                >
                  <span
                    className={`h-1 w-full rounded-full transition-colors ${
                      i <= stepIndex ? 'bg-blueSparkle' : 'bg-white/15'
                    }`}
                  />
                  <span
                    className={`hidden text-[10px] font-medium uppercase tracking-wide sm:block ${
                      i === stepIndex ? 'text-whiteout' : 'text-whiteout/40'
                    }`}
                  >
                    {STEP_LABELS[s]}
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={handleStartOver}
                title={c.startOverHint}
                className="ml-2 shrink-0 rounded-full border border-white/10 px-3 py-1 text-[11px] text-whiteout/40 transition-colors hover:border-red-400/40 hover:text-red-300"
              >
                {c.startOver}
              </button>
              <LangToggle variant="rail" />
            </div>
          </header>

          {/* Controls on one side, the booth on the other — for every step.
              The ONLY scrolling happens inside a step. */}
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
            {/* Narrow screens: the preview rides above the step as a band. */}
            <div className="shrink-0 border-b border-white/10 px-4 py-3 lg:hidden">
              <PreviewPane variant="band" />
            </div>

            <div className="order-2 min-h-0 flex-1 overflow-hidden lg:order-1">
              <div
                className="flex h-full transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${stepIndex * 100}%)` }}
              >
                <div className="h-full w-full shrink-0">
                  <StepBackgrounds onRegen={handleRegen} onRefine={handleRefine} />
                </div>
                <div className="h-full w-full shrink-0">
                  <StepBasics />
                </div>
                <div className="h-full w-full shrink-0">
                  <StepFramesV2
                    myFrames={myFrames}
                    onSavedFrame={(f) => setMyFrames((prev) => [f, ...prev])}
                    signedIn={Boolean(signedInEmail)}
                    onRequestSignIn={ensureSignIn}
                  />
                </div>
                <div className="h-full w-full shrink-0">
                  <StepFiltersV2
                    official={catalog.filters.official}
                    community={catalog.filters.community}
                    loading={catalogLoading}
                  />
                </div>
                <div className="h-full w-full shrink-0">
                  <StepEffects
                    signedIn={Boolean(signedInEmail)}
                    onRequestSignIn={() => void ensureSignIn()}
                  />
                </div>
              </div>
            </div>

            <aside className="order-1 hidden shrink-0 items-start justify-center overflow-y-auto soft-scrollbar border-l border-white/10 px-6 py-6 lg:order-2 lg:flex lg:w-[19rem]">
              <PreviewPane variant="rail" />
            </aside>
          </main>

          {/* Footer nav */}
          <footer className="shrink-0 border-t border-white/10 bg-black/80 px-6 py-3 backdrop-blur">
            <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => handleStepNav(stepIndex - 1)}
                disabled={stepIndex === 0 || submitting}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                  stepIndex === 0
                    ? 'invisible'
                    : 'text-whiteout/70 hover:bg-white/10 hover:text-whiteout'
                }`}
              >
                <FaArrowLeft className="text-xs" /> {c.back}
              </button>

              <div className="min-w-0 flex-1 text-center text-xs text-whiteout/40">
                {error ? (
                  <span className="text-red-400">{error}</span>
                ) : (
                  <span className="truncate">
                    {publicBoothHostLabel()}/<span className="text-whiteout/70">{slug || '…'}</span>
                    {signedInEmail && <> · {signedInEmail}</>}
                  </span>
                )}
              </div>

              {isLastStep ? (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!canCreate}
                  className="inline-flex items-center gap-2 rounded-full bg-blueSparkle px-7 py-2.5 text-sm font-semibold text-whiteout shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {submitting ? (
                    <>
                      <FaSpinner className="animate-spin" /> {c.creating}
                    </>
                  ) : signedInEmail ? (
                    <>{c.saveAndGoLive}</>
                  ) : (
                    <>
                      <FaGoogle /> {c.continueWithGoogle}
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleStepNav(stepIndex + 1)}
                  disabled={!canContinue}
                  className="inline-flex items-center gap-2 rounded-full bg-blueSparkle px-7 py-2.5 text-sm font-semibold text-whiteout shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {c.continueLabel} <FaArrowRight className="text-xs" />
                </button>
              )}
            </div>
          </footer>
        </>
      )}
    </div>
  )
}

export default Onboarding
